import type { InputHTMLAttributes, ReactElement } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import FormFieldShell from "./FormFieldShell";
import { cn } from "@/lib/utils";

export interface TextInputProps<TFieldValues extends FieldValues = any>
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "name" | "defaultValue" | "type" | "prefix" | "suffix"
  > {
  control?: Control<TFieldValues>;
  label?: string;
  name?: Path<TFieldValues>;
  prefix?: ReactElement;
  required?: boolean;
  suffix?: ReactElement;
  type?: HTMLInputElement["type"];
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  integerOnly?: boolean;
}

interface TextInputBaseProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix" | "suffix"> {
  label?: string;
  prefix?: ReactElement;
  required?: boolean;
  suffix?: ReactElement;
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  shouldShowError?: boolean;
  errorMessage?: string;
  integerOnly?: boolean;
}

function TextInputBase({
  className,
  id,
  label,
  labelClassName,
  errorClassName,
  errorMessage,
  prefix,
  required,
  shouldShowError,
  suffix,
  type = "text",
  wrapperClassName = "w-full",
  integerOnly,
  onKeyDown,
  inputMode,
  ...inputProps
}: Readonly<TextInputBaseProps>) {
  const inputId = id ?? label;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (integerOnly) {
      const isControlKey = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
        "Enter",
        "Escape",
      ].includes(e.key);
      const isShortcut = e.ctrlKey || e.metaKey;
      if (!/^\d$/.test(e.key) && !isControlKey && !isShortcut) {
        e.preventDefault();
      }
    }
    onKeyDown?.(e);
  };

  return (
    <FormFieldShell
      error={shouldShowError ? errorMessage : undefined}
      htmlFor={inputId}
      label={label}
      labelClassName={labelClassName}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <div className="relative flex items-center w-full">
        {prefix ? (
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center justify-center text-slate-400 dark:text-slate-500 pointer-events-none [&_svg]:w-4 [&_svg]:h-4">
            {prefix}
          </div>
        ) : null}
        <input
          {...inputProps}
          className={cn(
            "w-full h-10 px-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed",
            prefix && "pl-10",
            suffix && "pr-10",
            shouldShowError &&
              "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500",
            className
          )}
          id={inputId}
          type={type}
          inputMode={integerOnly ? "numeric" : inputMode}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            if (integerOnly) {
              e.target.value = e.target.value.replace(/\D/g, "");
            }
            inputProps.onChange?.(e);
          }}
        />
        {suffix ? (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center justify-center text-slate-400 dark:text-slate-500 [&_svg]:w-4 [&_svg]:h-4">
            {suffix}
          </div>
        ) : null}
      </div>
    </FormFieldShell>
  );
}

function TextInputControlled<TFieldValues extends FieldValues>({
  control,
  name,
  onChange,
  ...rest
}: Readonly<
  TextInputProps<TFieldValues> & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
  }
>) {
  const { field, fieldState, formState } = useController({
    control,
    name,
  });

  const shouldShowError =
    !!(fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;

  return (
    <TextInputBase
      {...field}
      {...rest}
      onChange={(e) => {
        if (rest.integerOnly) {
          e.target.value = e.target.value.replace(/\D/g, "");
        }
        field.onChange(e);
        onChange?.(e);
      }}
      id={rest.id ?? name}
      value={field.value ?? ""}
      shouldShowError={shouldShowError}
      errorMessage={fieldState.error?.message}
    />
  );
}

export function TextInput<TFieldValues extends FieldValues = any>(
  props: Readonly<TextInputProps<TFieldValues>>
) {
  if (props.control && props.name) {
    return (
      <TextInputControlled
        {...props}
        control={props.control}
        name={props.name}
      />
    );
  }
  return <TextInputBase {...props} />;
}

export default TextInput;
